import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../elements/Layout';
import RevenueComparisonChart from './RevenueComparison';
import CampaignCostsPieChart from './CampaignCostsPieChart';
import FinancialReport from './FinancialReport';
import AgingReport from './AgingReport';
import { getFinancialReport } from '../../utils/api/api';

// Lucide React icons
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Clock,
  FileText,
  LayoutDashboard
} from 'lucide-react';

const Reports = () => {
  const [report, setReport] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.4
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  const getReport = async () => {
    setIsLoading(true);
    try {
      const response = await getFinancialReport();
      setReport(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getReport();
  }, []);

  const tabs = [
    { id: 'all', label: 'All Reports', icon: <LayoutDashboard size={16} /> },
    { id: 'financial', label: 'Financial', icon: <FileText size={16} /> },
    { id: 'revenue', label: 'Revenue', icon: <TrendingUp size={16} /> },
    { id: 'expenses', label: 'Expenses', icon: <TrendingDown size={16} /> },
  ];

  return (
    <Layout text="Reports">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-full px-1 sm:px-4"
      >
        {/* Header with refresh button */}
        <motion.div
          variants={itemVariants}
          className="flex justify-between items-center mb-6"
        >
          <div>
            <h1 className="text-2xl font-medium text-gray-900 dark:text-white">
              Financial Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={getReport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl text-sm font-medium transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/50"
          >
            <RefreshCw size={16} />
            <span>Refresh</span>
          </motion.button>
        </motion.div>

        {/* Tab navigation */}
        <motion.div variants={itemVariants} className="mb-6 overflow-x-auto">
          <div className="inline-flex p-1 space-x-1 bg-gray-100 dark:bg-gray-800/50 rounded-xl">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                  }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        <AnimatePresence>
          {isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full mb-4"
              />
              <p className="text-gray-500 dark:text-gray-400">Loading financial data...</p>
            </motion.div>
          ) : (
            <>
              {/* Financial Report Card */}
              <motion.div
                variants={itemVariants}
                className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      <FileText size={16} />
                    </div>
                    <h2 className="font-medium text-gray-900 dark:text-white">Financial Summary</h2>
                  </div>
                  <div className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-2 py-1 rounded-full font-medium flex items-center">
                    <TrendingUp size={12} className="mr-1" />
                    <span>12% growth</span>
                  </div>
                </div>
                <div className="p-0 sm:p-2">
                  <FinancialReport financialData={report} />
                </div>
              </motion.div>

              {/* Charts Section */}
              <motion.div
                variants={itemVariants}
                className="flex flex-col md:flex-row gap-6 mb-6"
              >
                {/* Revenue Comparison Chart */}
                <motion.div
                  className="flex-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                >
                  <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <BarChart3 size={16} />
                      </div>
                      <h2 className="font-medium text-gray-900 dark:text-white">Revenue Comparison</h2>
                    </div>
                  </div>
                  <div className="p-5">
                    <RevenueComparisonChart />
                  </div>
                </motion.div>

                {/* Campaign Costs Chart */}
                <motion.div
                  className="flex-1 md:max-w-[40%] bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                >
                  <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center text-purple-600 dark:text-purple-400">
                        <PieChart size={16} />
                      </div>
                      <h2 className="font-medium text-gray-900 dark:text-white">Campaign Costs</h2>
                    </div>
                  </div>
                  <div className="p-5">
                    <CampaignCostsPieChart />
                  </div>
                </motion.div>
              </motion.div>

              <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center text-amber-600 dark:text-amber-400">
                      <Clock size={16} />
                    </div>
                    <h2 className="font-medium text-gray-900 dark:text-white">Aging Report</h2>
                  </div>
                </div>
                <AgingReport />
              </div>
            </>
          )}
        </AnimatePresence>
      </motion.div>
    </Layout>
  );
};

export default Reports;